import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGoogleAnalytics } from '../../../../common/hooks/useGoogleAnalytics';
import useTechnicalErrorMessage from '../../../../common/hooks/useTechnicalErrorMessage';
import { subscriptionAPI } from '../../../../../services/redwolfSdkAPI';

import * as Sentry from '@sentry/react';
import Modal from '../../../../common/components/Modal';
import Spinner from '../../../../common/components/Spinner';

import useRequestRedwolfService from '../../../../common/hooks/useRequestRedwolfService';
import { getToken } from '../../../../../utils/localStorage';

import { openNotification } from '../../../../../store/commonSlice';

import { useDispatch } from 'react-redux';

interface SubscriptionRenewModalProps {
	isRenewAppBundleModalOpen: boolean;
	isSkeletonLoading: boolean;
	plansList: any;
	subscriptionDetail: any;
	renewAppBundleSubscriptionModalToggle: (
		isRenewSubscriptionModalOpen: boolean,
		emptyPlansList: any,
		reRenderSubscriptionList: any
	) => void;
	updatePurchaseHistory: () => void;
}

const AppBundleRenewSubscription = ({
	isRenewAppBundleModalOpen,
	isSkeletonLoading,
	plansList,
	subscriptionDetail,
	renewAppBundleSubscriptionModalToggle,
	updatePurchaseHistory
}: SubscriptionRenewModalProps) => {
	const [isRenewConfirmModalOpen, setIsRenewConfirmModalOpen] = useState(false);
	const [planDetails, setPlanDetails] = useState<any>({});
	const [isSpinnerLoading, setIsSpinnerLoading] = useState(false);

	const navigate = useNavigate();
	const dispatch = useDispatch();

	const [showTechnicalErrorMessage] = useTechnicalErrorMessage();
	const [logGoogleAnalyticsEvent] = useGoogleAnalytics();
	const [requestRedwolfService] = useRequestRedwolfService();

	const token = getToken();

	const transaction = Sentry.startInactiveSpan({ name: 'Resume Subscription' });

	useEffect(() => {
		if (plansList.length) {
			const selectedPlan = plansList.find((plan: any) => plan.selected);
			if (selectedPlan) {
				setPlanDetails(selectedPlan);
			}
		}
	}, [plansList]);

	const handleRenewSubscription = (event: any) => {
		event.preventDefault();
		setIsRenewConfirmModalOpen(true);
	};

	const handleRenewSubscriptionConfirm = (event: any) => {
		event.preventDefault();
		setIsSpinnerLoading(true);
		logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_RENEW_SUBSCRIPTION_CONFIRMATION');

		const couponCode: any = null;
		const partnerAccountId: any = null;
		const noFreeTrial: any = false;

		requestRedwolfService(
			subscriptionAPI.reactivateSubscription(token, subscriptionDetail.subscriptionId)
		)
			.then((response: any) => {
				if (response.constructor.$metadata$.simpleName === 'Success') {
					dispatch(
						openNotification({
							type: 'SUCCESS',
							heading: 'Success',
							body: 'Subscription resumed.'
						})
					);
					if (
						subscriptionDetail.plan.planCode === planDetails.planCode ||
						subscriptionDetail.plan.planCode !== planDetails.planCode
					) {
						requestRedwolfService(
							subscriptionAPI.purchaseSubscription(
								token,
								planDetails.planCode,
								couponCode,
								noFreeTrial,
								partnerAccountId
							)
						)
							.then((purchaseSubscriptionResponse: any) => {
								setIsSpinnerLoading(false);
								if (purchaseSubscriptionResponse.constructor.$metadata$.simpleName === 'Success') {
									logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_RENEW_SUBSCRIPTION_SUCCESS');

									setIsRenewConfirmModalOpen(false);
									updatePurchaseHistory();
									renewAppBundleSubscriptionModalToggle(false, true, true);
									dispatch(
										openNotification({
											type: 'SUCCESS',
											heading: 'Success',
											body: 'Subscription resumed.'
										})
									);
								} else {
									Sentry.captureException(new Error(purchaseSubscriptionResponse));
								}
							})
							.catch((err: any) => {
								Sentry.captureException(err);
								setIsSpinnerLoading(false);
							});
					} else {
						logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_RENEW_SUBSCRIPTION_SUCCESS');
						setIsSpinnerLoading(false);
						setIsRenewConfirmModalOpen(false);
						updatePurchaseHistory();
						renewAppBundleSubscriptionModalToggle(false, true, true);
					}
					renewAppBundleSubscriptionModalToggle(false, true, true);

					navigate('/my-subscriptions');
				} else {
					logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_RENEW_SUBSCRIPTION_FAILURE', {
						error: 'Unable to process your request. Please try again.'
					});
					setIsSpinnerLoading(false);
					//showTechnicalErrorMessage();
					if (response.response.status !== 200) {
						dispatch(
							openNotification({
								type: 'ERROR',
								heading: 'Error',
								body: response.response.detail
							})
						);
					}
				}

				transaction && transaction.end();
			})
			.catch((err: any) => {
				transaction && transaction.end();

				logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_RENEW_SUBSCRIPTION_FAILURE', {
					error: 'Unable to process your request. Please try again.'
				});
				setIsSpinnerLoading(false);
				showTechnicalErrorMessage();
				Sentry.captureException(err);
			});
	};

	const handleRenewBack = (event: any) => {
		event.preventDefault();
		setIsRenewConfirmModalOpen(false);
	};

	const renewConfirmModalClose = () => {
		setIsRenewConfirmModalOpen(false);
		renewAppBundleSubscriptionModalToggle(false, true, null);
	};

	const renewModalClose = () => {
		renewAppBundleSubscriptionModalToggle(false, true, null);
	};

	if (plansList && subscriptionDetail && subscriptionDetail.plan && isRenewAppBundleModalOpen) {
		const isPlanExists = plansList.find(
			(plan: any) => plan.planCode === subscriptionDetail.plan.planCode
		);
		if (!isPlanExists) {
			plansList.push(subscriptionDetail.plan);
		}
	}

	let grandStartingTotal: any = 0;
	let billingInterval: any = '';

	if (Object.keys(planDetails).length > 0 && planDetails.planCode !== 'cancel') {
		billingInterval = planDetails.billingInterval;
		const basePlanPrice = parseFloat(planDetails.price.substring(1).replace(/,/g, ''));

		grandStartingTotal = basePlanPrice;
	}

	return (
		<>
			{subscriptionDetail && plansList && isRenewAppBundleModalOpen && !isRenewConfirmModalOpen && (
				<Modal
					containerId="renewSubscriptionModal"
					headingImg={subscriptionDetail.plan.imageUrl}
					show={isRenewAppBundleModalOpen}
					onHide={renewModalClose}
					heading="VIZIO Bundles"
					overlaycontainerClass="renewSubscription renew-bundles">
					<form className="edit-modal-form fieldn popupForm">
						<div className="plan_details">
							<div className="Plan-content">
								<h3 className="planDetails_heading">Current Plan</h3>
								<p className="planDetails_priceInfo">
									{`${subscriptionDetail.plan.name} `}
									<span className="planDetails_price">{`${subscriptionDetail.total}/${subscriptionDetail.plan.billingInterval}`}</span>
								</p>

								<p>(Ends {subscriptionDetail.billingDate.replace('Exp.', '')})</p>
							</div>
						</div>

						<div className="button-field-group">
							<button
								type="submit"
								className="primary-button"
								aria-label="Resume Subscription"
								id="renew-subscription-button"
								onClick={(event) => handleRenewSubscription(event)}>
								Resume Subscription
							</button>
						</div>
					</form>
				</Modal>
			)}

			{subscriptionDetail && planDetails && isRenewConfirmModalOpen && (
				<Modal
					containerId="renew-confirm-subscription"
					headingImg={subscriptionDetail.plan.imageUrl}
					show={isRenewConfirmModalOpen}
					onHide={renewConfirmModalClose}
					heading="VIZIO Bundles"
					containerClasses="renew-subscription"
					overlaycontainerClass="renewSubscription renew-bundles-confirm">
					{isSkeletonLoading ? (
						<Spinner />
					) : (
						<form className="edit-modal-form fieldn popupForm" id="confirm-renew-subscription">
							<div className="confirm-plan-container">
								<div className="confirm-details-container">
									<div className="confirm-plan-details">
										<div className="new-plan">Selected Plan</div>
										<div className="new-price">
											Billing will resume on {subscriptionDetail.billingDate.replace('Exp.', '')}.
										</div>
									</div>
								</div>
								<div className="addplan-total-amount">
									{Object.keys(planDetails).length > 0 && (
										<div className="bassic-plan-container">
											<p className="addplan-details">
												<span className="baseplan">Base Plan:</span>
												<span className="addon-value">{planDetails.name}</span>
											</p>

											<span className="select-pricedetails">
												{planDetails.price}/{billingInterval}
											</span>
										</div>
									)}
								</div>

								{Object.keys(planDetails).length > 0 && planDetails.planCode !== 'cancel' && (
									<div className="plan-start-details">
										Subscription Total:
										<span className="due-info">
											${grandStartingTotal.toFixed(2)}/{billingInterval}
										</span>
									</div>
								)}

								{
									<div className="free-trial-terms">
										<div className="title">Subscription Terms</div>
										<div className="free-trial-terms-details">
											This subscription will automatically renew until canceled, which you can do
											in the Subscriptions & Purchases section of your VIZIO Account settings. If
											you cancel this subscription, the cancelation will go into effect at the end
											of your current subscription period. To learn more about your subscriptions,
											visit{' '}
											<a href="/en/terms/terms-of-service" target="_blank" rel="noreferrer">
												vizio.com/terms
											</a>
											. Change your settings at{' '}
											<a href="/en/account" target="_blank" rel="noreferrer">
												vizio.com/account
											</a>
											.
										</div>
									</div>
								}
							</div>
							<div className="button-field-group m-btn">
								<button type="submit" className="secondary-button" onClick={handleRenewBack}>
									Back
								</button>
								<button
									type="submit"
									className="primary-button"
									onClick={(event) => handleRenewSubscriptionConfirm(event)}>
									Confirm
								</button>
							</div>
						</form>
					)}
				</Modal>
			)}

			{isSpinnerLoading && <Spinner />}
		</>
	);
};

export default AppBundleRenewSubscription;
